import { Controller } from "@hotwired/stimulus"

// Powers the filter UI on the GoRoadie Pro "Send Student Update" form:
//  * show/hide additional filters
//  * date range presets (All Time / Last 30 Days / This Year / Custom)
//  * live "this will send to N pupils" recipient count
export default class extends Controller {
  static targets = [
    "additionalFilters",
    "additionalFiltersToggle",
    "postcodeFilter",
    "enrolledPreset",
    "enrolledCustomRange",
    "enrolledAfter",
    "enrolledBefore",
    "dobPreset",
    "dobCustomRange",
    "dobAfter",
    "dobBefore",
    "recipientCount"
  ]

  static values = {
    countUrl: String
  }

  connect() {
    this.syncDatePresetsFromFields()
    this.refreshCount()
  }

  toggleAdditionalFilters(event) {
    event.preventDefault()
    if (!this.hasAdditionalFiltersTarget) return

    const isHidden = this.additionalFiltersTarget.classList.toggle("u-hidden")
    if (this.hasAdditionalFiltersToggleTarget) {
      this.additionalFiltersToggleTarget.textContent = isHidden ? "Show additional filters" : "Hide additional filters"
      this.additionalFiltersToggleTarget.setAttribute("aria-expanded", (!isHidden).toString())
    }
  }

  selectDatePreset(event) {
    event.preventDefault()
    this.applyDatePreset(event.currentTarget.dataset.presetGroup, event.currentTarget.dataset.preset)
    this.refreshCount()
  }

  markCustomDateRange(event) {
    const group = event.currentTarget === this.enrolledAfterTarget || event.currentTarget === this.enrolledBeforeTarget
      ? "enrolled"
      : "dob"
    this.applyDatePreset(group, "custom")
    this.refreshCount()
  }

  syncDatePresetsFromFields() {
    if (this.hasEnrolledAfterTarget) {
      this.applyDatePreset("enrolled", this.inferPreset(this.enrolledAfterTarget.value, this.enrolledBeforeTarget.value), { skipFill: true })
    }
    if (this.hasDobAfterTarget) {
      this.applyDatePreset("dob", this.inferPreset(this.dobAfterTarget.value, this.dobBeforeTarget.value), { skipFill: true })
    }
  }

  applyDatePreset(group, preset, options = {}) {
    const fields = this.dateFieldsFor(group)
    if (!fields) return

    if (!options.skipFill && preset !== "custom") {
      const dates = this.datePresets()[preset] || this.datePresets().all_time
      fields.after.value = dates.after
      fields.before.value = dates.before
    }

    if (fields.customRange) {
      fields.customRange.classList.toggle("u-hidden", preset !== "custom")
    }

    fields.buttons.forEach((button) => {
      const selected = button.dataset.preset === preset
      button.classList.toggle("Button--outline", !selected)
      button.setAttribute("aria-pressed", selected.toString())
    })
  }

  inferPreset(after, before) {
    const normalizedAfter = after || ""
    const normalizedBefore = before || ""
    if (!normalizedAfter && !normalizedBefore) return "all_time"

    const last30 = this.datePresets().last_30
    if (normalizedAfter === last30.after && normalizedBefore === last30.before) return "last_30"

    const thisYear = this.datePresets().this_year
    if (normalizedAfter === thisYear.after && !normalizedBefore) return "this_year"

    return "custom"
  }

  dateFieldsFor(group) {
    if (group === "enrolled" && this.hasEnrolledAfterTarget) {
      return {
        after: this.enrolledAfterTarget,
        before: this.enrolledBeforeTarget,
        customRange: this.hasEnrolledCustomRangeTarget ? this.enrolledCustomRangeTarget : null,
        buttons: this.hasEnrolledPresetTarget ? this.enrolledPresetTargets : []
      }
    }
    if (group === "dob" && this.hasDobAfterTarget) {
      return {
        after: this.dobAfterTarget,
        before: this.dobBeforeTarget,
        customRange: this.hasDobCustomRangeTarget ? this.dobCustomRangeTarget : null,
        buttons: this.hasDobPresetTarget ? this.dobPresetTargets : []
      }
    }
    return null
  }

  datePresets() {
    const today = this.startOfToday()
    const last30 = new Date(today)
    last30.setDate(last30.getDate() - 30)
    const yearStart = new Date(today.getFullYear(), 0, 1)

    return {
      all_time: { after: "", before: "" },
      last_30: { after: this.toISODate(last30), before: this.toISODate(today) },
      this_year: { after: this.toISODate(yearStart), before: "" }
    }
  }

  startOfToday() {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }

  toISODate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  refreshCount() {
    if (!this.hasRecipientCountTarget || !this.hasCountUrlValue) return

    clearTimeout(this._countTimer)
    this._countTimer = setTimeout(() => this.fetchCount(), 300)
  }

  fetchCount() {
    const params = new URLSearchParams()
    const add = (key, value) => {
      if (value != null && String(value).trim() !== "") params.append(`pro_messaging[${key}]`, value)
    }

    if (this.hasPostcodeFilterTarget) add("postcode_filter", this.postcodeFilterTarget.value)
    if (this.hasEnrolledAfterTarget) add("enrolled_on_or_after", this.enrolledAfterTarget.value)
    if (this.hasEnrolledBeforeTarget) add("enrolled_on_or_before", this.enrolledBeforeTarget.value)
    if (this.hasDobAfterTarget) add("dob_on_or_after", this.dobAfterTarget.value)
    if (this.hasDobBeforeTarget) add("dob_on_or_before", this.dobBeforeTarget.value)

    fetch(`${this.countUrlValue}?${params.toString()}`, {
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin"
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || !this.hasRecipientCountTarget) return
        const count = data.count
        const noun = count === 1 ? "pupil" : "pupils"
        this.recipientCountTarget.textContent = `This message will be sent to ${count} active ${noun}.`
      })
      .catch(() => {
        // Leave the default message in place if the count lookup fails.
      })
  }
}
